import {
  Component, Input, Output, EventEmitter, OnChanges, SimpleChanges
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-quote-preview-v2',
  standalone: true,
  imports: [CommonModule, TranslateModule, RouterModule],
  templateUrl: './quote-preview-v2.component.html'
})
export class QuotePreviewV2Component implements OnChanges {
  // -------------------- Inputs / Outputs --------------------
  @Input() quote: any;
  @Input() fiatCode = 'USD';
  @Input() fiatRates: Record<string, number> = { USD: 1 };
  @Input() swapStatus: 'idle' | 'approve' | 'pending' | 'success' | 'error' | 'cancelled' = 'idle';
  @Input() swapHash: string | null = null;
  @Input() swapError: string | null = null;

  @Input() swapDisabled = false;

  /** MetaMask (ou parent) peut injecter le gas concret (gating) */
  @Input() gasPriceWei: bigint = 0n;
  @Input() fullGasWei: bigint = 0n;

  /** Facultatif: validation / gating d’une alternative côté parent */
  @Input() validateAlternative?: (route: any) => { disabled: boolean; reasons: string[] };

  @Output() executeRequest = new EventEmitter<void>();      // exécuter la quote courante
  @Output() selectAlternative = new EventEmitter<any>();    // l’utilisateur choisit une route alternative
  @Output() close = new EventEmitter<void>();
  // ---- Compat V1 (optionnels) ----
  @Input() signature: string = '';
  @Input() nativeTokenUsd: number = 0;                   // pas utilisé pour l’instant
  @Output() copyToClipboard = new EventEmitter<string>(); // V1 envoyait la signature
  @Output() signRequest = new EventEmitter<void>();       // pour déclencher une simu/sign

  // -------------------- UI state --------------------
  showMainDetails = true; // on montre ouvert par défaut (comme Uniswap)
  expandedAlt: Record<string, boolean> = {}; // chevrons par alt
  showAlternatives = false;

  private autoHide?: any;
  private pendingTimer?: any;

  constructor(private i18n: TranslateService) { }

  // -------------------- Life cycle --------------------
  ngOnChanges(changes: SimpleChanges): void {
    clearTimeout(this.autoHide);
    clearTimeout(this.pendingTimer);

    if (this.swapStatus === 'pending') {
      this.pendingTimer = setTimeout(() => {
        if (this.swapStatus === 'pending') this.swapStatus = 'idle';
      }, 30_000);
    } else if (this.swapStatus !== 'idle') {
      this.autoHide = setTimeout(() => {
        if (this.swapStatus !== 'pending') this.swapStatus = 'idle';
      }, 5_000);
    }
  }

  // -------------------- Helpers génériques --------------------
  private toNum(v: any): number {
    return typeof v === 'bigint' ? Number(v) : Number(v ?? 0);
  }

  private localeFor(code: string): string {
    const map: Record<string, string> = { EUR: 'fr-FR', USD: 'en-US', XOF: 'fr-FR', CFA: 'fr-FR' };
    return map[code.toUpperCase()] || (navigator.language || 'en-US');
  }

  formatAmount(raw: string | number | bigint, maxDecimals = 6): string {
    if (raw === '' || raw == null) return '—';
    const n = Number(raw);
    if (!isFinite(n)) return '—';
    let min = 0, max = maxDecimals;
    const abs = Math.abs(n);
    if (abs >= 1) { min = 2; max = Math.min(max, 4); }
    else if (abs >= 0.01) { min = 2; max = Math.min(max, 6); }
    else { min = 4; max = Math.min(max, 8); }
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: min, maximumFractionDigits: max }).format(n);
  }

  getTokenLabel(sym: string): string {
    const key = 'TOKENS.' + (sym || '').toUpperCase();
    const t = this.i18n.instant(key);
    return t !== key ? t : (sym || '—');
  }

  // -------------------- Données principales --------------------
  get sellTokenSymbol(): string { return this.quote?.sellTokenSymbol ?? this.quote?.SellTokenSymbol ?? '—'; }
  get buyTokenSymbol(): string { return this.quote?.buyTokenSymbol ?? this.quote?.BuyTokenSymbol ?? '—'; }

  get displayedSellAmount(): number {
    return this.toNum(this.quote?.formattedSellAmount ?? this.quote?.FormattedSellAmount);
  }
  get displayedBuyAmount(): number {
    return this.toNum(this.quote?.formattedBuyAmount ?? this.quote?.FormattedBuyAmount);
  }

  /** Logo/provider */
  get swapIconUrl(): string | null {
    return (
      this.quote?.toolLogoURI || this.quote?.ToolLogoURI ||
      this.quote?.toolDetails?.logoURI || this.quote?.icon || this.quote?.Icon || null
    );
  }
  get dexSource(): string {
    const raw =
      this.quote?.toolKey || this.quote?.toolName ||
      this.quote?.source || this.quote?.Source || '—';
    return String(raw).charAt(0).toUpperCase() + String(raw).slice(1);
  }

  /** Taux de conversion façon Uniswap : "1 BUY = X SELL" (ex: 1 ETH = 4 734 USDC) */
  get conversionRateText(): string {
    const buy = this.displayedBuyAmount;
    const sell = this.displayedSellAmount;
    if (buy > 0 && sell > 0) {
      const rate = sell / buy; // combien de SELL pour 1 BUY
      return `1 ${this.getTokenLabel(this.buyTokenSymbol)} = ${this.formatAmount(rate, 4)} ${this.getTokenLabel(this.sellTokenSymbol)}`;
    }
    // fallback si on a un texte déjà fourni
    return this.quote?.priceText || '—';
  }

  /** Fiat des montants */
  getBuyAmountFiat(): string {
    const priceUsd = this.toNum(this.quote?.buyTokenUsdPrice);
    const amt = this.displayedBuyAmount;
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const value = priceUsd * amt * rate;
    if (!value) return '';
    return value.toLocaleString(this.localeFor(this.fiatCode), { style: 'currency', currency: this.fiatCode, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  getSellAmountFiat(): string {
    const priceUsd = this.toNum(this.quote?.sellTokenUsdPrice);
    const amt = this.displayedSellAmount;
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    const value = priceUsd * amt * rate;
    if (!value) return '';
    return value.toLocaleString(this.localeFor(this.fiatCode), { style: 'currency', currency: this.fiatCode, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // -------------------- Frais réseau (main + routes) --------------------
  private canonicalNativeSymbol(chainId?: number, raw?: string): string {
    const map: Record<number, string> = { 1: 'ETH', 10: 'ETH', 56: 'BNB', 137: 'MATIC', 42161: 'ETH', 8453: 'ETH' };
    if (chainId && map[chainId]) return map[chainId];
    const up = (raw || '').toUpperCase();
    if (up === 'POL' || up === 'MATIC') return map[137];
    return up || 'ETH';
  }

  /** Ligne "Coût du réseau" – LiFi (natifs & fiat) */
  get lifiFeeRow(): { textEth?: string; textFiat?: string } {
    const chainId = Number(this.quote?.fromChainId ?? this.quote?.chainId);
    const sym = this.canonicalNativeSymbol(chainId, this.quote?.GasTokenSymbol || this.quote?.gasTokenSymbol);

    const native = this.toNum(this.quote?.lifiNetworkFeeNative ?? 0); // exposé par ton back
    const usd = this.toNum(this.quote?.lifiNetworkFeeUsd ?? 0);

    const textEth = native ? `${this.formatAmount(native, 6)} ${sym}` : undefined;
    const textFiat = usd ? `≈ ${this.formatAmount(usd * (this.fiatRates?.[this.fiatCode] || 1), 2)} ${this.fiatCode}` : undefined;
    return { textEth, textFiat };
  }

  /** Détails alternatifs : calcul “LiFi” par route */
  getRouteLifiFee(r: any): { textEth?: string; textFiat?: string } {
    // wei direct, sinon price * gasLimit
    let feeWei = this.toNum(r?.lifiGasAmountWei ?? r?.LifiGasAmountWei);
    if (!feeWei) {
      const p = this.toNum(r?.lifiGasPriceWei ?? r?.LifiGasPriceWei);
      const l = this.toNum(r?.lifiEstimatedGas ?? r?.LifiEstimatedGas);
      if (p && l) feeWei = p * l;
    }

    const chainId = Number(this.quote?.fromChainId ?? r?.fromChainId ?? r?.chainId);
    const sym = this.canonicalNativeSymbol(chainId, r?.GasTokenSymbol || r?.gasTokenSymbol);
    const dec = this.toNum(r?.GasTokenDecimals ?? r?.gasTokenDecimals) || 18;
    const native = feeWei ? (feeWei / Math.pow(10, dec)) : 0;

    let usd = this.toNum(r?.lifiGasFeeUsd ?? r?.LifiGasFeeUsd);
    if (!usd && native) {
      const price = this.toNum(r?.lifiGasTokenPriceUsd ?? r?.LifiGasTokenPriceUsd) ||
        this.toNum(this.quote?.gasTokenPriceUsd ?? this.quote?.GasTokenPriceUsd);
      if (price) usd = native * price;
    }

    const rate = this.fiatRates?.[this.fiatCode] || 1;
    return {
      textEth: native ? `${this.formatAmount(native, 8)} ${sym}` : undefined,
      textFiat: usd ? `≈ ${(usd * rate).toLocaleString(this.localeFor(this.fiatCode), { style: 'currency', currency: this.fiatCode })}` : undefined
    };
  }

  // -------------------- Alternatives --------------------
 keyOf(x: any): string {
    return String(
      x?.providerKey ?? x?.ProviderKey ??
      x?.toolKey ?? x?.ToolKey ??
      x?.tool ?? x?.Tool ??
      x?.source ?? x?.Source ??
      x?.providerName ?? x?.ProviderName ??
      x?.name ?? ''
    ).toLowerCase().trim();
  }
  getRouteLogo(r: any): string | null {
    return r?.providerLogo || r?.toolLogoURI || r?.ToolLogoURI ||
      r?.toolDetails?.logoURI || r?.logoURI || r?.icon || r?.Icon || null;
  }
  getRouteProvider(r: any): string {
    return (
      r?.providerName ??
      r?.ProviderName ??
      this.keyOf(r) ??
      '—'
    );
  }

  getRouteBuySymbol(r: any): string {
    return r?.buyTokenSymbol || r?.toTokenSymbol || r?.action?.toToken?.symbol || this.buyTokenSymbol || '—';
  }
  getRouteEstimatedBuy(r: any): string {
    if (r?.formattedBuyAmount) {
      return `${this.formatAmount(r.formattedBuyAmount, 8)} ${this.getRouteBuySymbol(r)}`;
    }
    const raw = this.toNum(r?.toAmountRaw ?? r?.ToAmountRaw ?? r?.toAmount);
    if (!raw) return '—';
    const dec = this.toNum(r?.toTokenDecimals ?? r?.toToken?.decimals ?? this.quote?.buyTokenDecimals ?? 18) || 18;
    const val = raw / Math.pow(10, dec);
    return `${this.formatAmount(val, 8)} ${this.getRouteBuySymbol(r)}`;
  }
  getRouteEstimatedBuyFiat(r: any): string {
    let usd = r?.toAmountUsd != null ? this.toNum(r.toAmountUsd) : NaN;
    if (!isFinite(usd)) {
      const price = this.toNum(r?.buyTokenUsdPrice ?? this.quote?.buyTokenUsdPrice);
      const txt = this.getRouteEstimatedBuy(r);
      const num = this.toNum(txt.split(' ')[0]);
      usd = isFinite(num) && price ? num * price : 0;
    }
    if (!usd) return '';
    const rate = this.fiatRates?.[this.fiatCode] || 1;
    return '≈ ' + (usd * rate).toLocaleString(this.localeFor(this.fiatCode), { style: 'currency', currency: this.fiatCode });
  }
  isExecutableRoute(r: any): boolean {
    const tx = r?.transactionRequest ?? r?.TransactionRequest ?? null;
    const hasTx = !!(tx?.to && tx?.data) || !!(r?.to && r?.data) || !!(r?.target && r?.data);
    const hasTyped = !!(r?.domain && r?.types && r?.value);
    return hasTx || hasTyped;
  }
  isAlternativeDisabled(r: any): boolean {
    if (this.swapDisabled) return true;
    if (!this.isExecutableRoute(r)) return true;
    if (!this.validateAlternative) return false;
    return this.validateAlternative(r).disabled;
  }

  get otherRoutes(): any[] {
    const all = Array.isArray(this.quote?.singleRoutes) ? this.quote.singleRoutes : [];
    const currentKey = this.keyOf(this.quote || {});
    return all.filter((r: any) => this.keyOf(r) !== currentKey);
  }

  toggleAlt(r: any): void {
    const k = this.keyOf(r);
    this.expandedAlt[k] = !this.expandedAlt[k];
  }

  chooseAlternative(r: any): void {
    if (this.isAlternativeDisabled(r)) return;
    this.selectAlternative.emit(r);
  }

  // -------------------- Divers --------------------
  isUserCancelled(err: any): boolean {
    if (!err) return false;
    const str = String(
      err?.reason || err?.shortMessage || err?.error?.message ||
      err?.data?.message || err?.message || ''
    ).toLowerCase();
    return str.includes('action rejected') || str.includes('user denied') || str.includes('user rejected') ||
      str.includes('rejected by user') || str.includes('request rejected') ||
      str.includes('cancelled') || str.includes('canceled') || String(err?.code) === '4001';
  }

  onClose(): void { this.close.emit(); }
}
